import { createContext, useContext, useEffect, useState, ReactNode, useRef } from 'react';
import Keycloak from 'keycloak-js';
import { directGrantConfig } from '../config/keycloak.config';

interface KeycloakContextType {
  keycloak: Keycloak | null;
  authenticated: boolean;
  initialized: boolean;
  login: () => void;
  logout: () => void;
  loginWithPassword: (username: string, password: string) => Promise<void>;
  token: string | undefined;
  refreshToken: () => Promise<boolean>;
}

const KeycloakContext = createContext<KeycloakContextType | undefined>(undefined);

interface KeycloakAuthProviderProps {
  children: ReactNode;
  config: {
    url: string;
    realm: string;
    clientId: string;
  };
}

export function KeycloakAuthProvider({ children, config }: KeycloakAuthProviderProps) {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const tokenRefreshInterval = useRef<number | null>(null);

  useEffect(() => {
    const kc = new Keycloak({
      url: config.url,
      realm: config.realm,
      clientId: config.clientId,
    });

    // Check for stored tokens first
    const storedToken = localStorage.getItem('kc_token');
    const storedRefreshToken = localStorage.getItem('kc_refreshToken');
    const storedIdToken = localStorage.getItem('kc_idToken');

    // Initialize Keycloak
    kc.init({
      onLoad: 'check-sso',
      // silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html',
      pkceMethod: 'S256',
      checkLoginIframe: false,
      // Load stored tokens if available
      token: storedToken || undefined,
      refreshToken: storedRefreshToken || undefined,
      idToken: storedIdToken || undefined,
    })
      .then((auth) => {
        console.log('Keycloak init success, authenticated:', auth);
        setKeycloak(kc);
        
        // If we have stored tokens but Keycloak says not authenticated, 
        // try to validate the stored token
        if (!auth && storedToken) {
          try {
            const tokenParsed = JSON.parse(atob(storedToken.split('.')[1]));
            const currentTime = Math.floor(Date.now() / 1000);
            
            // Check if token is still valid
            if (tokenParsed.exp > currentTime) {
              console.log('Using stored valid token');
              kc.token = storedToken;
              kc.refreshToken = storedRefreshToken || undefined;
              kc.idToken = storedIdToken || undefined;
              kc.tokenParsed = tokenParsed;
              setAuthenticated(true);
            } else {
              console.log('Stored token expired, clearing');
              localStorage.removeItem('kc_token');
              localStorage.removeItem('kc_refreshToken');
              localStorage.removeItem('kc_idToken');
              setAuthenticated(false);
            }
          } catch (e) {
            console.error('Error parsing stored token:', e);
            localStorage.removeItem('kc_token');
            localStorage.removeItem('kc_refreshToken');
            localStorage.removeItem('kc_idToken');
            setAuthenticated(false);
          }
        } else {
          setAuthenticated(auth);
        }
        
        setInitialized(true);

        // Store tokens if authenticated
        if (auth && kc.token) {
          localStorage.setItem('kc_token', kc.token);
          localStorage.setItem('kc_refreshToken', kc.refreshToken || '');
          localStorage.setItem('kc_idToken', kc.idToken || '');
          
          // WICHTIG: Token auch im Cookie speichern für SSR Server Actions
          document.cookie = `kc_token=${kc.token}; path=/; max-age=3600; SameSite=Strict`;
        }

        // Auto-refresh token
        if (kc.token) {
          tokenRefreshInterval.current = setInterval(() => {
            kc.updateToken(70)
              .then((refreshed) => {
                if (refreshed && kc.token) {
                  console.log('Token refreshed');
                  localStorage.setItem('kc_token', kc.token);
                  localStorage.setItem('kc_refreshToken', kc.refreshToken || '');
                  localStorage.setItem('kc_idToken', kc.idToken || '');
                  
                  // Token auch im Cookie aktualisieren
                  document.cookie = `kc_token=${kc.token}; path=/; max-age=3600; SameSite=Strict`;
                }
              })
              .catch(() => {
                console.error('Failed to refresh token');
                // Clear stored tokens on refresh failure
                localStorage.removeItem('kc_token');
                localStorage.removeItem('kc_refreshToken');
                localStorage.removeItem('kc_idToken');
                document.cookie = 'kc_token=; path=/; max-age=0';
                setAuthenticated(false);
              });
          }, 60000); // Check every minute
        }
      })
      .catch((error) => {
        console.error('Keycloak init failed', error);
        setInitialized(true);
        setAuthenticated(false);
      });
      
    // Cleanup function
    return () => {
      if (tokenRefreshInterval.current) {
        clearInterval(tokenRefreshInterval.current);
      }
    };
  }, [config.url, config.realm, config.clientId]);

  const login = () => {
    keycloak?.login();
  };

  const logout = () => {
    // Clear stored tokens
    localStorage.removeItem('kc_token');
    localStorage.removeItem('kc_refreshToken');
    localStorage.removeItem('kc_idToken');
    
    // Clear cookie
    document.cookie = 'kc_token=; path=/; max-age=0';
    
    // Clear any timeout intervals for token refresh
    if (tokenRefreshInterval.current) {
      clearInterval(tokenRefreshInterval.current);
      tokenRefreshInterval.current = null;
    }
    
    // Logout from Keycloak (this will clear the SSO session)
    if (keycloak) {
      keycloak.logout({
        redirectUri: window.location.origin + '/login'
      });
    } else {
      // Fallback if keycloak is not initialized
      window.location.href = '/login';
    }
  };

  const loginWithPassword = async (username: string, password: string): Promise<void> => {
    try {
      const response = await fetch(directGrantConfig.tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: config.clientId,
          username: username,
          password: password,
        }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const tokenData = await response.json();
      
      // Store tokens in Keycloak instance
      if (keycloak) {
        keycloak.token = tokenData.access_token;
        keycloak.refreshToken = tokenData.refresh_token;
        keycloak.tokenParsed = JSON.parse(atob(tokenData.access_token.split('.')[1]));
        
        // Update authentication state
        setAuthenticated(true);
        
        // Store tokens in localStorage for persistence (consistent naming)
        localStorage.setItem('kc_token', tokenData.access_token);
        localStorage.setItem('kc_refreshToken', tokenData.refresh_token);
        localStorage.setItem('kc_idToken', tokenData.id_token || '');
      }
    } catch (error) {
      console.error('Direct login error:', error);
      throw error;
    }
  };

  const refreshToken = async (): Promise<boolean> => {
    if (!keycloak) return false;
    try {
      return await keycloak.updateToken(30);
    } catch {
      return false;
    }
  };

  const value: KeycloakContextType = {
    keycloak,
    authenticated,
    initialized,
    login,
    logout,
    loginWithPassword,
    token: keycloak?.token,
    refreshToken,
  };

  // Always render children, even during initialization
  // This prevents blocking the UI while Keycloak initializes
  return <KeycloakContext.Provider value={value}>{children}</KeycloakContext.Provider>;
}

export function useKeycloak() {
  const context = useContext(KeycloakContext);
  if (context === undefined) {
    throw new Error('useKeycloak must be used within a KeycloakAuthProvider');
  }
  return context;
}
